import { AirlineLogo, carrierTheme } from './AirlineLogo'
import { lookupAirport, airportTimeZone } from '../lib/airports'
import type { FlightLookupResult } from '../lib/flightLookup'

/**
 * A flight returned by the lookup, drawn as the real thing.
 *
 * The search result is the moment someone confirms this is their flight, so it
 * shows everything they'd check on a boarding pass — both ends with local dates
 * and times, the duration, the aircraft — rather than a line of text they have
 * to trust. Same carrier theming as the saved card, so what you pick looks like
 * what you get.
 */
export default function FlightResultCard({
  result,
  selected,
  onSelect,
}: {
  result: FlightLookupResult
  selected?: boolean
  onSelect?: () => void
}) {
  const theme = carrierTheme(result.airlineCode)
  const originZone = airportTimeZone(result.originIATA)
  const destinationZone = airportTimeZone(result.destinationIATA)
  const origin = lookupAirport(result.originIATA)
  const destination = lookupAirport(result.destinationIATA)

  const muted = theme ? theme.muted : 'var(--text-secondary)'
  const surface = theme
    ? { background: theme.background, color: theme.text, borderColor: 'transparent' }
    : {}

  const content = (
    <>
      <div className="spread" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div className="stack" style={{ gap: 2, minWidth: 0 }}>
          <span className="t-spec-caption" style={{ color: muted }}>
            {result.flightNumber}
            {result.status ? ` · ${result.status}` : ''}
          </span>
          <span className="t-display-m" style={{ whiteSpace: 'nowrap' }}>
            {result.originIATA} — {result.destinationIATA}
          </span>
        </div>
        <AirlineLogo code={result.airlineCode} variant="icon" size={46} onDark={!!theme} />
      </div>

      {/* Both ends, each in its own local time — the only way an overnight or a
          dateline crossing reads correctly. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gap: 12,
          alignItems: 'center',
          marginTop: 14,
        }}
      >
        <End
          eyebrow="Departs"
          city={origin?.city ?? result.originCity}
          date={formatDate(result.departureUTC, originZone)}
          time={formatTime(result.departureUTC, originZone)}
          zone={zoneAbbreviation(originZone, result.departureUTC)}
          muted={muted}
        />

        <div className="stack" style={{ gap: 3, alignItems: 'center' }}>
          <span
            aria-hidden
            style={{ width: 34, height: 1, background: 'currentColor', opacity: 0.28 }}
          />
          <span className="t-spec-caption" style={{ color: muted, whiteSpace: 'nowrap' }}>
            {duration(result)}
          </span>
          <span
            aria-hidden
            style={{ width: 34, height: 1, background: 'currentColor', opacity: 0.28 }}
          />
        </div>

        <End
          eyebrow="Arrives"
          city={destination?.city ?? result.destinationCity}
          date={formatDate(result.arrivalUTC, destinationZone)}
          time={formatTime(result.arrivalUTC, destinationZone)}
          zone={zoneAbbreviation(destinationZone, result.arrivalUTC)}
          muted={muted}
          nextDay={result.arrivesNextDay}
          alignEnd
        />
      </div>

      {(result.aircraft || selected) && (
        <div
          className="row"
          style={{
            gap: 8,
            marginTop: 14,
            paddingTop: 12,
            borderTop: `1px solid ${theme ? 'rgba(255,255,255,0.14)' : 'var(--hairline)'}`,
            flexWrap: 'wrap',
          }}
        >
          {result.aircraft && (
            <span className="t-spec-caption" style={{ color: muted }}>
              {result.aircraft}
            </span>
          )}
          {selected && <span className="chip chip-positive">Using this flight</span>}
        </div>
      )}
    </>
  )

  const style: React.CSSProperties = {
    padding: 18,
    textAlign: 'left',
    width: '100%',
    display: 'block',
    borderWidth: selected ? 'var(--signal-rule)' : undefined,
    borderColor: selected ? 'var(--accent)' : undefined,
    ...surface,
  }

  if (!onSelect) {
    return (
      <div className="panel" style={style}>
        {content}
      </div>
    )
  }

  return (
    <button className="panel" style={style} onClick={onSelect}>
      {content}
    </button>
  )
}

function End({
  eyebrow,
  city,
  date,
  time,
  zone,
  muted,
  nextDay,
  alignEnd,
}: {
  eyebrow: string
  city: string
  date: string
  time: string
  zone: string
  muted: string
  nextDay?: boolean
  alignEnd?: boolean
}) {
  return (
    <div
      className="stack"
      style={{ gap: 1, minWidth: 0, alignItems: alignEnd ? 'flex-end' : 'flex-start' }}
    >
      <span className="t-eyebrow" style={{ color: muted }}>
        {eyebrow}
      </span>
      <span className="t-timecode" style={{ fontSize: '1.2rem' }}>
        {time}
        {nextDay && (
          <span
            className="t-spec-caption"
            style={{ color: 'var(--accent)', marginLeft: 4, verticalAlign: 'super' }}
          >
            +1
          </span>
        )}
      </span>
      <span className="t-spec-caption" style={{ color: muted, whiteSpace: 'nowrap' }}>
        {date} · {zone}
      </span>
      <span className="t-caption truncate" style={{ color: muted, maxWidth: '100%' }}>
        {city}
      </span>
    </div>
  )
}

function formatTime(date: Date, timeZone: string | null): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

function formatDate(date: Date, timeZone: string | null): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

function zoneAbbreviation(timeZone: string | null, at: Date): string {
  if (!timeZone) return ''
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(at)
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/** Real elapsed time, so it's right across zones and the dateline. */
function duration(result: FlightLookupResult): string {
  const seconds = (result.arrivalUTC.getTime() - result.departureUTC.getTime()) / 1000
  if (seconds <= 0) return '—'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}
